import type { ReactNode } from "react"

type Theme = "blue" | "orange" | "gray"

interface CalcShellProps {
  /** Icon rendered inside the colored tile */
  icon: ReactNode
  /** Uppercase tiny label above the title (e.g. "Calculator") */
  eyebrow: string
  /** The main card title */
  title: string
  /** Theme color for the icon tile and eyebrow */
  theme?: Theme
  /** Optional additional classes on the outer card */
  className?: string
  /** Card body */
  children: ReactNode
}

const THEMES: Record<Theme, { bg: string; text: string }> = {
  blue: { bg: "bg-[#0469f0]/10", text: "text-[#0469f0]" },
  orange: { bg: "bg-[#ff9951]/10", text: "text-[#ff9951]" },
  gray: { bg: "bg-gray-200/60", text: "text-gray-700" },
}

/**
 * CalcShell — standard calculator / input panel card used across tool pages.
 *
 * Renders a white rounded card with a header row (colored icon tile + eyebrow +
 * title) and a content slot. Used by every variant at Fridge.com for
 * form panels, result panels, preview panels, and sidebar cards.
 */
export function CalcShell({
  icon,
  eyebrow,
  title,
  theme = "blue",
  className = "",
  children,
}: CalcShellProps) {
  const t = THEMES[theme]
  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 shadow-[0_1px_3px_rgba(0,25,69,0.04)] p-5 md:p-6 ${className}`}
    >
      <div className="flex items-center gap-2.5 mb-4 pb-4 border-b border-gray-100">
        <div className={`w-9 h-9 rounded-lg ${t.bg} flex items-center justify-center flex-shrink-0`}>
          {icon}
        </div>
        <div>
          <p className={`text-[10px] uppercase tracking-widest ${t.text} font-bold leading-none mb-0.5`}>
            {eyebrow}
          </p>
          <h2 className="text-base font-black tracking-tight text-gray-900 leading-tight">
            {title}
          </h2>
        </div>
      </div>
      {children}
    </div>
  )
}
